import React, { useState, useCallback, useEffect } from 'react';
import {
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Users,
  CheckCircle,
  Leaf,
  Award,
  Filter
} from 'lucide-react';
import { getApiUrl } from '../utils/config';
// Utility function to capitalize words
const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// API configuration
const API_BASE = getApiUrl();
const CACHE_KEY_FLOWER = 'weederboard_public_flower_deals';
const CACHE_KEY_CONCENTRATES = 'weederboard_public_concentrate_deals';
const CACHE_DATE_KEY = 'weederboard_public_deals_date';

// Tab configuration
const TABS = [
  { id: 'flower', label: 'All Flower', icon: Leaf },
  { id: 'concentrates', label: 'All Concentrates', icon: TrendingUp }
];

// Utility functions for caching
const getCachedDeals = (type, state = '') => {
  try {
    const cacheKey = state ? `${type}_${state}` : type;
    const cachedDate = localStorage.getItem(`${CACHE_DATE_KEY}_${cacheKey}`);
    const today = new Date().toDateString();
    
    // Check if cache is from today
    if (cachedDate === today) {
      const fullCacheKey = state ? 
        (type === 'flower' ? `weederboard_public_flower_deals_${state}` : `weederboard_public_concentrate_deals_${state}`) :
        (type === 'flower' ? CACHE_KEY_FLOWER : CACHE_KEY_CONCENTRATES);
      const cachedDeals = localStorage.getItem(fullCacheKey);
      if (cachedDeals) {
        return JSON.parse(cachedDeals);
      }
    }
    return null;
  } catch (error) {
    console.error('Error reading cached deals:', error);
    return null;
  }
};

const setCachedDeals = (deals, type, state = '') => {
  try {
    const cacheKey = state ? `${type}_${state}` : type;
    const today = new Date().toDateString();
    const fullCacheKey = state ? 
      (type === 'flower' ? `weederboard_public_flower_deals_${state}` : `weederboard_public_concentrate_deals_${state}`) :
      (type === 'flower' ? CACHE_KEY_FLOWER : CACHE_KEY_CONCENTRATES);
    localStorage.setItem(fullCacheKey, JSON.stringify(deals));
    localStorage.setItem(`${CACHE_DATE_KEY}_${cacheKey}`, today);
  } catch (error) {
    console.error('Error caching deals:', error);
  }
};

// Fallback sample data for flower deals
const FALLBACK_FLOWER_DEALS = [
  {
    id: 1,
    strain: "Blue Dream",
    brand: "Premium Gardens",
    origin_dispensary: "Green Valley Dispensary",
    cost: 45.99,
    weight: "1/8 oz",
    thc_percentage: "22.5",
    type: "Flower",
    image_url: null
  },
  {
    id: 2,
    strain: "Wedding Cake", 
    brand: "Top Shelf Co.",
    origin_dispensary: "Cannabis Corner",
    cost: 38.50,
    weight: "1/8 oz",
    thc_percentage: "26.8",
    type: "Flower",
    image_url: null
  },
  {
    id: 3,
    strain: "Gelato #33",
    brand: "Craft Cannabis", 
    origin_dispensary: "The Herb Shop",
    cost: 42.00,
    weight: "1/8 oz",
    thc_percentage: "24.1",
    type: "Flower",
    image_url: null
  }
];

// Fallback sample data for concentrate deals
const FALLBACK_CONCENTRATE_DEALS = [
  {
    id: 4,
    strain: "GMO",
    brand: "Live Resin Labs",
    origin_dispensary: "Green Valley Dispensary",
    cost: 65.99,
    weight: "1g",
    type: "Live Resin",
    image_url: null
  },
  {
    id: 5,
    strain: "Wedding Cake",
    brand: "Rosin Co.",
    origin_dispensary: "Cannabis Corner",
    cost: 72.50,
    weight: "1g",
    type: "Live Rosin",
    image_url: null
  },
  {
    id: 6,
    strain: "Gelato #33",
    brand: "Concentrate Kings",
    origin_dispensary: "The Herb Shop",
    cost: 58.00,
    weight: "1g",
    type: "Badder",
    image_url: null
  }
];

const FEATURES = [
  {
    icon: TrendingUp,
    title: "Daily Deal Tracking",
    description: "Find the best prices for your favorite cannabis products across multiple dispensaries. Updated daily!",
    premium: false  
  },
  {
    icon: Filter,
    title: "Advanced Filtering",
    description: "Find your favorite strains, shop your favorite brands, and see what your favorite dispensaries have to offer.",
    premium: true
  },
  {
    icon: Users,
    title: "Community Reviews",
    description: "Access verified reviews and ratings from our community of cannabis enthusiasts. (coming soon)",
    premium: true
  },
 
];

const PRICING_TIERS = [
  
  {
    name: "14-Day Free Trial",
    price: "$0.00!",
    period: "then $4.20/month",
    description: "Full access to all premium features for 14 days!",
    features: [
      "Full access to all premium features",
      "Advanced filtering & sorting",
      "Search by strain, brand, location or dispensary",
      "Cancel anytime during trial",
    ],
    buttonText: "Start Free Trial Now!",
    buttonStyle: "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg",
    popular: true
  }
];

// Deal Card Component for public deals
const PublicDealCard = React.memo(({ deal, index, isBlurred = false }) => {
  const formatPrice = (price) => `$${parseFloat(price).toFixed(2)}`;
  
  return (
    <div className={`bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 ${isBlurred ? 'filter blur-sm' : ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Left side - Rank, Image, and Product Info */}
        <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
              {index + 1}
            </div>
          </div>
          
          {/* Product Image */}
          {deal.image_url && (
            <div className="flex-shrink-0">
              <img 
                src={deal.image_url} 
                alt={`${deal.strain} by ${deal.brand}`}
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight">{deal.strain}</h3>
            <p className="text-gray-600 font-medium text-sm sm:text-base">{deal.brand}</p>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{deal.origin_dispensary}</p>
            <div className="flex flex-wrap gap-1 sm:gap-2 mt-2">
              {deal.type && (
                <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                  {deal.type}
                </span>
              )}
              {deal.weight && (
                <span className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium">
                  {deal.weight}
                </span>
              )}
              {deal.thc_percentage && (
                <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium">
                  {deal.thc_percentage}% THC
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Right side - Price and Action */}
        <div className="flex flex-col sm:items-end space-y-2 sm:min-w-0">
          <div className="text-xl sm:text-2xl font-bold text-green-600">{formatPrice(deal.cost)}</div>
          {deal.product_link && (
            <button
              onClick={() => window.open(deal.product_link, '_blank', 'noopener,noreferrer')}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200"
            >
              View Deal
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// Feature Card Component
const FeatureCard = React.memo(({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 relative">
      {feature.premium && (
        <div className="absolute top-2 right-2">
          <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold">
            PREMIUM
          </span>
        </div>
      )}
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            feature.premium 
              ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-700' 
              : 'bg-gradient-to-r from-green-100 to-emerald-200 text-green-700'
          }`}>
            <Icon size={24} />
          </div>
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-lg mb-2">{feature.title}</h3>
          <p className="text-gray-600">{feature.description}</p>
        </div>
      </div>
    </div>
  );
});

// Pricing Card Component
const PricingCard = React.memo(({ tier, onSelectPlan }) => {
  const handleSelectPlan = useCallback(() => {
    onSelectPlan(tier);
  }, [tier, onSelectPlan]);

  return (
    <div className={`border-2 rounded-xl p-8 shadow-lg transition-all duration-300 hover:shadow-xl relative ${
      tier.popular 
        ? 'border-green-500 scale-105 lg:scale-110' 
        : 'border-gray-200 hover:border-green-300'
    }`}>
      {tier.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        </div>
      )}
      
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
        <div className="mb-4">
          <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
          {tier.price !== "$0" && <span className="text-gray-500 ml-2">{tier.period}</span>}
        </div>
        <p className="text-gray-600">{tier.description}</p>
      </div>
      
      <ul className="space-y-4 mb-8">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-3">
            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
            <span className="text-sm text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>
      
      <button
        onClick={handleSelectPlan}
        className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${tier.buttonStyle}`}
      >
        {tier.buttonText}
      </button>
    </div>
  );
});

const LandingPage = ({ onSignUp, onSignIn, message, messageType = 'info' }) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('flower');
  
  // State for public deals
  const [publicDeals, setPublicDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [dealsError, setDealsError] = useState(null);
  
  // Add state filter
  const [selectedState, setSelectedState] = useState('');
  const [availableStates, setAvailableStates] = useState([]);

  // Fetch available states for filtering
  const fetchAvailableStates = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/filters/states/${activeTab}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAvailableStates(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  }, [activeTab]);

  // Fetch public deals from API with state filtering
  const fetchPublicDeals = useCallback(async (type = 'flower', state = '') => {
    // First check cache (but only if no state filter)
    if (!state) {
      const cachedDeals = getCachedDeals(type);
      if (cachedDeals && cachedDeals.length > 0) {
        setPublicDeals(cachedDeals);
        setDealsLoading(false);
        return;
      }
    }

    // If no cache or state filter, fetch from API
    try {
      setDealsLoading(true);
      setDealsError(null);
      
      // Use the original endpoints with state parameter
      const endpoint = type === 'concentrates' ? '/public/deals' : '/public/flower-deals';
      
      // Build query parameters
      const params = new URLSearchParams();
      if (state) {
        params.append('states', state);
      }
      
      const url = `${API_BASE}${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.length > 0) {
          // Capitalize brand names in the data
          const processedData = data.data.map(deal => ({
            ...deal,
            brand: capitalizeWords(deal.brand)
          }));
          
          // Cache the deals for the day (only if no state filter)
          if (!state) {
            setCachedDeals(processedData, type);
          }
          // Show all deals from API (up to 10)
          setPublicDeals(processedData);
        } else {
          // No deals returned
          if (state) {
            // State filter returned no results - show empty state
            setPublicDeals([]);
            setDealsError(null);
          } else {
            // No state filter and no deals - use fallback data
            const fallbackData = type === 'flower' ? FALLBACK_FLOWER_DEALS : FALLBACK_CONCENTRATE_DEALS;
            setPublicDeals(fallbackData);
            setDealsError('Using sample data - API returned no deals');
          }
        }
      } else {
        throw new Error(`API responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching public deals:', error);
      if (state) {
        // State filter with error - show empty state
        setPublicDeals([]);
        setDealsError("I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com");
      } else {
        // No state filter with error - use fallback data
        setDealsError("I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com");
        const fallbackData = type === 'flower' ? FALLBACK_FLOWER_DEALS : FALLBACK_CONCENTRATE_DEALS;
        setPublicDeals(fallbackData);
      }
    } finally {
      setDealsLoading(false);
    }
  }, []);

  // Handle tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setSelectedState(''); // Reset state filter when changing tabs
    fetchPublicDeals(tabId, '');
  }, [fetchPublicDeals]);

  // Handle state filter change
  const handleStateChange = useCallback((state) => {
    setSelectedState(state);
    fetchPublicDeals(activeTab, state);
  }, [activeTab, fetchPublicDeals]);

  // Fetch deals and states on component mount
  useEffect(() => {
    fetchPublicDeals(activeTab, selectedState);
    fetchAvailableStates();
  }, [fetchPublicDeals, fetchAvailableStates, activeTab, selectedState]);

  const handleSelectPlan = useCallback((plan) => {
    if (plan.name === 'Free') {
      onSignUp('free');
    } else if (plan.name === 'Premium' || plan.name === 'Monthly Subscription') {
      onSignUp('premium');
    } else {
      // Handle Pro plan - could open contact form or redirect to sales
      window.open('mailto:sales@weederboard.com?subject=Pro Plan Inquiry', '_blank');
    }
  }, [onSignUp]);

  const handleGetStarted = useCallback(() => {
    onSignUp('premium');
  }, [onSignUp]);

  const handleSignIn = useCallback(() => {
    onSignIn();
  }, [onSignIn]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <Leaf className="text-green-600" size={32} />
              <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                WeederBoard
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={handleSignIn}
                className="text-gray-700 hover:text-green-600 font-medium transition-colors text-sm sm:text-base"
              >
                Sign In
              </button>
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 sm:px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300 text-sm sm:text-base"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-8">
              Find the Best Deals on Cannabis in New England
            </h1>
            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-600 mb-8">
            Search by strain, brand, location or dispensary. Compare prices across dispensaries and save money every day.
            </h3>
            {/* Free Trial Badge */}
            <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-400 text-yellow-900 px-6 py-3 rounded-full font-bold text-lg mb-8 shadow-lg">
              <span className="mr-2">🎉</span>
              <span>14-Day Free Trial!</span>
            </div>            {/* Message Display */}
            {message && (
              <div className={`max-w-2xl mx-auto mb-8 p-4 rounded-lg ${
                messageType === 'warning' 
                  ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' 
                  : messageType === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}>
                <p className="font-medium">{message}</p>
              </div>
            )}
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Whether you're looking for a rare strain, flower from a particular farm, the best deals on concentrates, or the cheapest flower in your area, we've got you covered. 
              Join the community of smart cannabis consumers saving money every day on their favorite products.
            </p>
            
            {/* Trial Benefits */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-12 max-w-4xl mx-auto shadow-lg border border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Start Your Free Trial Today and Get:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                  <span className="font-semibold text-gray-900">Full Access</span>
                  <span className="text-sm text-gray-600">All premium features</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                    <Filter className="text-blue-600" size={24} />
                  </div>
                  <span className="font-semibold text-gray-900">Advanced Filters</span>
                  <span className="text-sm text-gray-600">Find exactly what you want</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <span className="font-semibold text-gray-900">No Commitment</span>
                  <span className="text-sm text-gray-600">Cancel anytime</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <span>Start Free Trial</span>
                <ChevronRight size={20} />
              </button>
              <button
                onClick={handleSignIn}
                className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-50 transition-all duration-300"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Today's Deals Preview */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Today's Top Deals
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's a preview of today's best deals - premium members get access to thousands more!
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-8 bg-white p-2 rounded-2xl shadow-lg max-w-2xl mx-auto">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* State Filter */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Find the best deal in your state!
                </h3>
                <p className="text-sm text-gray-600">
                  Filter deals by state to see what's available near you
                </p>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <div className="relative">
                  <select
                    value={selectedState}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-w-[150px]"
                  >
                    <option value="">All States</option>
                    {availableStates.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" />
                </div>
                {selectedState && (
                  <button
                    onClick={() => handleStateChange('')}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Loading State */}
          {dealsLoading && (
            <div className="max-w-4xl mx-auto text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
              <p className="text-gray-600">
                Loading {selectedState ? `${selectedState} ` : ''}deals...
              </p>
            </div>
          )}
          
          {/* Error State */}
          {dealsError && !dealsLoading && (
            <div className="max-w-4xl mx-auto mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-center text-sm">
                <strong>Note:</strong> {dealsError}
              </p>
            </div>
          )}
          
          {/* Deals Display */}
          {!dealsLoading && publicDeals.length > 0 && (
            <div className="max-w-4xl mx-auto space-y-4 relative">
              {/* Filter Status */}
              {selectedState && (
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">
                    Showing deals from <span className="font-semibold text-green-600">{selectedState}</span>
                  </p>
                </div>
              )}
              
              {publicDeals.map((deal, index) => (
                <PublicDealCard 
                  key={deal.id || `deal-${index}`}
                  deal={deal} 
                  index={index}
                  isBlurred={publicDeals.length > 10 && index >= 10}
                />
              ))}
              
              {/* Overlay for last blurred item only */}
              {publicDeals.length > 10 && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/90 to-transparent flex items-center justify-center" style={{ height: '200px' }}>
                  <div className="text-center bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
                    <Award className="text-green-600 mx-auto mb-4" size={48} />
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Unlock All Deals
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md">
                      Start your free trial to get access to hundreds of deals updated daily across all dispensaries
                      {selectedState && ` in ${selectedState}`}. 
                      {publicDeals.length > 10 && ` Plus ${publicDeals.length - 10} more deals available!`}
                    </p>
                    <button
                      onClick={handleGetStarted}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300"
                    >
                      Sign up Now!
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Encouragement section when we have fewer than 10 deals */}
          {!dealsLoading && publicDeals.length > 0 && publicDeals.length <= 10 && (
            <div className="max-w-4xl mx-auto mt-8">
              <div className="text-center bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200">
                <Award className="text-green-600 mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Want to See More Deals?
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Start your free trial to get access to hundreds of deals updated daily across all dispensaries
                  {selectedState && ` in ${selectedState}`}. 
                  {publicDeals.length < 10 && ` We have ${10 - publicDeals.length} more deals available for premium members!`}
                </p>
                <button
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300"
                >
                  Start Free Trial
                </button>
              </div>
            </div>
          )}

          {/* No Deals State */}
          {!dealsLoading && publicDeals.length === 0 && (
            <div className="max-w-4xl mx-auto text-center py-12">
              <Award className="text-gray-400 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {selectedState 
                  ? `Sorry! There are no deals we can find in ${selectedState}`
                  : "No deals available right now"
                }
              </h3>
              <p className="text-gray-600 mb-6">
                {selectedState 
                  ? "Try selecting a different state or check back later for new deals in this area!"
                  : "Check back later for the latest cannabis deals"
                }
              </p>
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all duration-300"
              >
                Start Free Trial
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Smart Shoppers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse offerings across dispensaries, find your favorite strains, and save money on your favorite products. 
              <span className="font-semibold text-green-600"> Start your free trial to unlock all features!</span>
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Start Your Free Trial Today!
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get full access to all premium features for 14 days!
            </p>
          </div>
          
          <div className="flex justify-center max-w-7xl mx-auto">
            <div className="w-full max-w-md">
              {PRICING_TIERS.map((tier, index) => (
                <PricingCard 
                  key={index} 
                  tier={tier} 
                  onSelectPlan={handleSelectPlan}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Ready to Start Your Free Trial?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Join a community of smart cannabis consumers saving money every day!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                Start Free Trial Now!
              </button>
              <button
                onClick={handleSignIn}
                className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all duration-300"
              >
                Already have an account?
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Leaf className="text-green-400" size={24} />
                <span className="text-xl font-bold">WeederBoard</span>
              </div>
              <p className="text-gray-400">
                The smartest way to find deals on cannabis products and save money.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 WeederBoard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
